/**
 * Breach check against Have I Been Pwned "Pwned Passwords" with k-anonymity: only the first five
 * hex characters of the password's SHA-1 leave the device. The service answers with every known
 * suffix for that prefix (padded with fake rows), and the match happens locally.
 */

export const PWNED_RANGE_URL = "https://api.pwnedpasswords.com/range/";
const PREFIX_LENGTH = 5;
const MAX_PARALLEL = 4;

export type BreachErrorCode = "network" | "bad-response";

export class BreachCheckError extends Error {
  public readonly code: BreachErrorCode;

  public constructor(code: BreachErrorCode, message: string) {
    super(message);
    this.name = "BreachCheckError";
    this.code = code;
  }
}

export type Fetcher = (url: string, init: RequestInit) => Promise<Response>;
export type Sha1 = (input: string) => Promise<string>;

export interface BreachChecker {
  /** How often the password appears in known breaches (0 = not found). */
  readonly count: (password: string) => Promise<number>;
  /** Counts for several passwords; duplicates and shared prefixes cost one request. */
  readonly countMany: (passwords: ReadonlyArray<string>) => Promise<ReadonlyMap<string, number>>;
  /** Drops cached ranges, e.g. when the vault locks. */
  readonly clear: () => void;
}

/** Uppercase hex SHA-1, the format the range API uses. */
export async function sha1Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest), (b: number): string => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

/**
 * Parses a range response ("SUFFIX:COUNT" per line). Padding rows carry count 0 and are dropped.
 * Throws on anything that does not look like a range body.
 */
export function parseRange(body: string): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const raw of body.split("\n")) {
    const line = raw.trim();
    if (line === "") {
      continue;
    }
    const match = /^([0-9A-F]{35}):(\d+)$/i.exec(line);
    const suffix = match?.[1];
    const count = match?.[2];
    if (suffix === undefined || count === undefined) {
      throw new BreachCheckError("bad-response", "Unerwartete Antwort von Have I Been Pwned.");
    }
    const n = Number(count);
    if (n > 0) {
      counts.set(suffix.toUpperCase(), n);
    }
  }
  return counts;
}

async function inBatches<T>(items: ReadonlyArray<T>, size: number, run: (item: T) => Promise<void>): Promise<void> {
  for (let i = 0; i < items.length; i += size) {
    await Promise.all(items.slice(i, i + size).map(run));
  }
}

export function createBreachChecker(deps: { readonly fetch: Fetcher; readonly sha1?: Sha1; readonly endpoint?: string }): BreachChecker {
  const sha1 = deps.sha1 ?? sha1Hex;
  const endpoint = deps.endpoint ?? PWNED_RANGE_URL;
  const ranges = new Map<string, Promise<ReadonlyMap<string, number>>>();

  async function fetchRange(prefix: string): Promise<ReadonlyMap<string, number>> {
    let response: Response;
    try {
      response = await deps.fetch(`${endpoint}${prefix}`, {
        method: "GET",
        // Padding hides how many suffixes the prefix really has; no-store keeps prefixes out of the disk cache.
        headers: { "Add-Padding": "true" },
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer"
      });
    } catch {
      throw new BreachCheckError("network", "Have I Been Pwned ist nicht erreichbar.");
    }
    if (!response.ok) {
      throw new BreachCheckError("bad-response", `Have I Been Pwned antwortet mit Status ${response.status}.`);
    }
    return parseRange(await response.text());
  }

  function range(prefix: string): Promise<ReadonlyMap<string, number>> {
    const cached = ranges.get(prefix);
    if (cached !== undefined) {
      return cached;
    }
    const pending = fetchRange(prefix);
    ranges.set(prefix, pending);
    // Failed lookups are retried next time instead of sticking.
    pending.catch((): void => {
      ranges.delete(prefix);
    });
    return pending;
  }

  async function countMany(passwords: ReadonlyArray<string>): Promise<ReadonlyMap<string, number>> {
    const unique = [...new Set(passwords.filter((p: string): boolean => p !== ""))];
    const hashes = new Map<string, string>();
    await Promise.all(unique.map(async (p: string): Promise<void> => {
      hashes.set(p, await sha1(p));
    }));
    const prefixes = [...new Set([...hashes.values()].map((h: string): string => h.slice(0, PREFIX_LENGTH)))];
    await inBatches(prefixes, MAX_PARALLEL, async (prefix: string): Promise<void> => {
      await range(prefix);
    });
    const result = new Map<string, number>();
    for (const [password, hash] of hashes) {
      const counts = await range(hash.slice(0, PREFIX_LENGTH));
      result.set(password, counts.get(hash.slice(PREFIX_LENGTH)) ?? 0);
    }
    return result;
  }

  return {
    count: async (password: string): Promise<number> => (await countMany([password])).get(password) ?? 0,
    countMany,
    clear: (): void => ranges.clear()
  };
}

/** The app-wide checker. Uses the global fetch, bound so it works when called detached. */
export const breachChecker: BreachChecker = createBreachChecker({
  fetch: (url: string, init: RequestInit): Promise<Response> => fetch(url, init)
});

/** "12.345-mal" style count for messages. */
export function formatBreachCount(count: number): string {
  return `${new Intl.NumberFormat("de-DE").format(count)}-mal`;
}
